/**
 * Страница «Отчёты» — журнал разборов из reports/*.md чекаута.
 *
 * Порождается воркфлоу concept-review: review-<дата>.md (что изменили и
 * почему) и concept.md (текстовая версия модели для автора концепта).
 * Ученик/команда читают по ссылке, не открывая репозиторий.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert, Anchor, Box, Container, Divider, Group, Loader, NavLink, Paper,
    Stack, Text, Title,
} from '@mantine/core';
import { IconArrowLeft, IconFileText } from '@tabler/icons-react';
import { fetchReports, fetchReportFile } from '../lib/api-client';
import { MarkdownText } from '../components/MarkdownText';
import { registerChatContext } from '../stores/chat-store';
import { HelpMenu } from '../components/HelpMenu';
import { REPORTS_TOUR } from '../lib/tours';

export function ReportsPage(): JSX.Element {
    const { owner, repo } = useParams();
    const [params] = useSearchParams();
    const ref  = params.get('ref')  ?? undefined;
    const path = params.get('path') ?? undefined;
    const backQuery = (() => {
        const q = new URLSearchParams();
        if (ref) q.set('ref', ref);
        if (path) q.set('path', path);
        const qs = q.toString();
        return qs ? '?' + qs : '';
    })();
    const [selected, setSelected] = useState<string | null>(null);

    // Контекст глобального чата (App-level): агент знает, что открыты Отчёты.
    useEffect(() => {
        if (owner && repo) {
            registerChatContext({ owner, repo, gitRef: ref, path, pageContext: { view: 'reports' } });
        }
    });

    const listQ = useQuery({
        queryKey: ['reports', owner, repo, ref, path],
        queryFn:  () => fetchReports({ owner: owner!, repo: repo!, ref, path }),
        enabled:  !!owner && !!repo,
        retry:    false,
    });

    const reports = listQ.data?.reports ?? [];
    const active  = selected ?? reports[0]?.file ?? null;

    const fileQ = useQuery({
        queryKey: ['report-file', owner, repo, ref, path, active],
        queryFn:  () => fetchReportFile({ owner: owner!, repo: repo!, ref, path, file: active! }),
        enabled:  !!owner && !!repo && !!active,
        retry:    false,
    });

    return (
        <Box>
            <Box
                component="header"
                style={{
                    position: 'sticky', top: 0, zIndex: 100,
                    background: 'var(--mantine-color-body)',
                    borderBottom: '1px solid var(--mantine-color-default-border)',
                    padding: '12px 16px',
                }}
            >
                <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Group gap="sm" wrap="nowrap">
                        <Anchor component={Link} to={`/r/${owner}/${repo}${backQuery}`} c="dimmed" data-tour="back-to-narrative">
                            <Group gap={4} wrap="nowrap"><IconArrowLeft size={16} /><Text fz="sm">к нарративу</Text></Group>
                        </Anchor>
                        <Divider orientation="vertical" />
                        <Title order={4} style={{ lineHeight: 1 }}>Отчёты разборов</Title>
                    </Group>
                    <HelpMenu tour={REPORTS_TOUR} />
                </Group>
            </Box>

            <Container size="lg" py="md">
                {listQ.isLoading && <Loader />}
                {listQ.error && (
                    <Alert color="red" title="Не удалось загрузить отчёты">
                        {(listQ.error as Error).message}
                    </Alert>
                )}
                {!listQ.isLoading && reports.length === 0 && !listQ.error && (
                    <Alert color="gray" title="Отчётов пока нет">
                        Папка reports/ пуста. Отчёты появляются после разбора концепта —
                        см. воркбук concept-review.md (фаза 4: review-&lt;дата&gt;.md + concept.md).
                    </Alert>
                )}
                {reports.length > 0 && (
                    <Group align="flex-start" gap="md" wrap="nowrap">
                        <Stack gap={2} style={{ width: 240, flexShrink: 0 }} data-tour="reports-list">
                            {reports.map(r => (
                                <NavLink
                                    key={r.file}
                                    active={r.file === active}
                                    label={r.file}
                                    leftSection={<IconFileText size={14} />}
                                    onClick={() => setSelected(r.file)}
                                    style={{ borderRadius: 6 }}
                                />
                            ))}
                        </Stack>
                        <Paper withBorder p="lg" radius="md" style={{ flex: 1, minWidth: 0 }} data-tour="reports-content">
                            {fileQ.isLoading && <Loader size="sm" />}
                            {fileQ.error && (
                                <Alert color="red">{(fileQ.error as Error).message}</Alert>
                            )}
                            {fileQ.data && <MarkdownText text={fileQ.data.content} />}
                        </Paper>
                    </Group>
                )}
            </Container>
        </Box>
    );
}
